/**
 * StartupPortalAuthContext — magic-link session for founders.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  PORTAL_API,
  PORTAL_REFRESH_FAILED,
  portalApi,
  setPortalAccessToken,
} from "@/lib/portalApi";
import silentRefresh from "@/lib/silentRefresh";

const PortalAuthCtx = createContext(null);

const needsStartupRefresh = (pathname) => pathname.startsWith("/portal/startup");

export const StartupPortalAuthProvider = ({ children }) => {
  const { pathname } = useLocation();
  const [phase, setPhase] = useState(() =>
    needsStartupRefresh(typeof window !== "undefined" ? window.location.pathname : "") ? "checking" : "anonymous",
  );
  const [session, setSession] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!needsStartupRefresh(pathname)) {
      setPortalAccessToken(null);
      setSession(null);
      setPhase("anonymous");
      return undefined;
    }

    (async () => {
      const data = await silentRefresh(`${PORTAL_API}/session/refresh`);
      if (cancelled) return;
      if (!data?.access_token) {
        setPortalAccessToken(null);
        setPhase("anonymous");
        return;
      }
      setPortalAccessToken(data.access_token);
      try {
        const { data: me } = await portalApi.get("/me");
        if (cancelled) return;
        setSession(me);
        setPhase("authenticated");
      } catch (_e) {
        if (cancelled) return;
        setPortalAccessToken(null);
        setPhase("anonymous");
      }
    })();

    return () => { cancelled = true; };
  }, [pathname]);

  useEffect(() => {
    const onFail = () => {
      setPortalAccessToken(null);
      setSession(null);
      setPhase("anonymous");
    };
    window.addEventListener(PORTAL_REFRESH_FAILED, onFail);
    return () => window.removeEventListener(PORTAL_REFRESH_FAILED, onFail);
  }, []);

  const completeLogin = useCallback(async (verifyResponse) => {
    setPortalAccessToken(verifyResponse.access_token);
    const { data: me } = await portalApi.get("/me");
    setSession(me);
    setPhase("authenticated");
    return me;
  }, []);

  const refreshSession = useCallback(async () => {
    const { data: me } = await portalApi.get("/me");
    setSession(me);
    return me;
  }, []);

  const logout = useCallback(async () => {
    try {
      await portalApi.post("/logout");
    } catch (_e) {
      /* ignore */
    }
    setPortalAccessToken(null);
    setSession(null);
    setPhase("anonymous");
  }, []);

  return (
    <PortalAuthCtx.Provider
      value={{
        phase,
        session,
        isAuthenticated: phase === "authenticated",
        completeLogin,
        refreshSession,
        logout,
      }}
    >
      {children}
    </PortalAuthCtx.Provider>
  );
};

export const useStartupPortalAuth = () => {
  const ctx = useContext(PortalAuthCtx);
  if (!ctx) {
    throw new Error(
      "useStartupPortalAuth() must be used inside <StartupPortalAuthProvider>",
    );
  }
  return ctx;
};

export default StartupPortalAuthProvider;
